import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { ChatRecord, ChatStatus } from "../ChatRecord";

interface UseChatOptions {
  /**
   * Organization ID for the request
   */
  orgId: string;

  /**
   * Whether to load messages with the chat record
   * @default true
   */
  loadMessages?: boolean;

  /**
   * Polling interval in milliseconds when assistant is generating
   * @default 1000
   */
  pollingIntervalMs?: number;
}

/**
 * Hook to fetch a chat by ID with automatic polling.
 *
 * Polls the chat endpoint every 1 second (configurable) until the most recent
 * assistant message is completed. Stops polling when:
 * - The last message has role === ChatRole.ASSISTANT
 * - AND status === ChatMessageStatus.COMPLETED
 *
 * @param chatId - The ID of the chat to fetch
 * @param options - Configuration options
 * @returns React Query result with chat data
 */
export function useChat(
  chatId: string | undefined,
  options: UseChatOptions,
): UseQueryResult<ChatRecord, Error> {
  const { chatService } = useDomainServices();
  const queryClient = useQueryClient();
  const { orgId, loadMessages = true, pollingIntervalMs = 500 } = options;

  return useQuery<ChatRecord, Error>({
    queryKey: ["chat", chatId, loadMessages],
    queryFn: async ({ signal, queryKey }) => {
      if (chatId === undefined) {
        return Promise.reject(new Error("Invalid chatId"));
      }

      // Access previous data from React Query's cache
      const previousData = queryClient.getQueryData<ChatRecord>(queryKey);

      if (previousData?.continuation_token) {
        // Use delta update if we have previous data with continuation token
        return await chatService.updateChatFromDeltas(previousData, {
          orgId,
          abortSignal: signal,
        });
      }

      // First fetch or no continuation token - get full data
      return await chatService.getChatById(chatId, loadMessages, {
        orgId,
        abortSignal: signal,
      });
    },
    enabled: chatId !== undefined && orgId !== undefined,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: (query) => {
      const data = query.state.data;

      const shouldPoll = data?.status !== ChatStatus.USER_TURN;

      return shouldPoll ? pollingIntervalMs : false;
    },
  });
}
